import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiImage, FiX, FiChevronLeft, FiChevronRight, FiArrowLeft } from 'react-icons/fi';
import { getImageUrl } from '../../utils/imageUtils';
import { formatarData } from '../../utils/dateUtils';

const AlbumViagem = () => {
  const { viagem_id } = useParams();
  const navigate = useNavigate();
  const [fotos, setFotos] = useState([]);
  const [viagem, setViagem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [indiceAtual, setIndiceAtual] = useState(0);

  useEffect(() => {
    fetchFotos();
  }, [viagem_id]);

  const fetchFotos = async () => {
    try {
      const [fotosResponse, viagensResponse] = await Promise.all([
        axios.get(`/api/site/galeria-fotos/viagem/${viagem_id}`),
        axios.get('/api/viagens')
      ]);
      
      setFotos(fotosResponse.data);
      const viagemEncontrada = viagensResponse.data.find(v => v.id === parseInt(viagem_id));
      setViagem(viagemEncontrada);
    } catch (error) {
      console.error('Erro ao carregar fotos:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirFoto = (foto, index) => {
    setFotoSelecionada(foto);
    setIndiceAtual(index);
  };

  const proximaFoto = () => {
    const novoIndice = (indiceAtual + 1) % fotos.length;
    setIndiceAtual(novoIndice);
    setFotoSelecionada(fotos[novoIndice]);
  };

  const fotoAnterior = () => {
    const novoIndice = (indiceAtual - 1 + fotos.length) % fotos.length;
    setIndiceAtual(novoIndice);
    setFotoSelecionada(fotos[novoIndice]);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-sky-600 to-sky-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/galeria-fotos')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft size={20} />
            Voltar para Galeria
          </button>
          <h1 className="text-5xl font-bold mb-4">
            {viagem ? viagem.titulo : 'Álbum de Fotos'}
          </h1>
          {viagem && (
            <div className="text-xl text-sky-100">
              <p>{viagem.destino}</p>
              <p>{formatarData(viagem.data_ida)}</p>
            </div>
          )}
        </div>
      </section>

      {/* Fotos */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : fotos.length === 0 ? (
            <div className="text-center py-12">
              <FiImage className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-600 text-lg">Nenhuma foto disponível para esta viagem.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {fotos.map((foto, index) => (
                <div
                  key={foto.id}
                  className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity group"
                  onClick={() => abrirFoto(foto, index)}
                >
                  <img
                    src={getImageUrl(foto.url)}
                    alt={foto.titulo || 'Foto'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <FiImage className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                  </div>
                  {foto.titulo && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-white text-sm font-medium">{foto.titulo}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal de Foto */}
      {fotoSelecionada && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setFotoSelecionada(null)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh]">
            <button
              onClick={() => setFotoSelecionada(null)}
              className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <FiX size={24} />
            </button>
            {fotos.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fotoAnterior();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-colors"
                >
                  <FiChevronLeft size={24} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    proximaFoto();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-colors"
                >
                  <FiChevronRight size={24} />
                </button>
              </>
            )}
            <img
              src={getImageUrl(fotoSelecionada.url)}
              alt={fotoSelecionada.titulo || 'Foto'}
              className="max-w-full max-h-[90vh] mx-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {fotoSelecionada.titulo && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-6 py-3 rounded-lg">
                <p className="text-gray-900 font-medium text-center">{fotoSelecionada.titulo}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlbumViagem;
